import { useQuery } from '@tanstack/react-query';
import { encodeDateForUrl } from '../../utils/dateUtils';
import operationAPI from '../../api/operationApi';
import { IOperations } from '../../type/operation';
import { addDays } from 'date-fns';
import calenderApi from '../../api/calenderApi';
import { IBookingAvailability } from '../../type/BookingAvailability';
import { IForecastEntryOption } from '../../type/forecastEntryOption';
import { ITemperature } from '../../type/temperature';
import { ITodayTemperature } from '../../type/todayTemperature';

/**
 * Fetcher for flight details
 */
export const fetchFlightDetails = async (
  operationDate: Date,
  ownerId: number,
): Promise<any[]> => {
  const encodedDate = encodeDateForUrl(operationDate);
  const response = await operationAPI.getFlightsDetails(encodedDate, ownerId);
  return response.data;
};

/**
 * React Query hook for flight details
 */
export const useFlightDetails = (
  operationDate: Date | null,
  ownerId: number | null,
) => {
  return useQuery({
    queryKey: ['flights', operationDate, ownerId],
    queryFn: () => fetchFlightDetails(operationDate!, ownerId!),
    enabled: Boolean(operationDate && ownerId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Fetcher for cancellation reasons
 */
export const fetchCancelReasons = async (): Promise<any[]> => {
  const response = await operationAPI.getCancelreasonDetails();
  return response.data;
};

/**
 * React Query hook for cancellation reasons
 */
export const useCancelReasons = () => {
  return useQuery({
    queryKey: ['cancelReasons'],
    queryFn: fetchCancelReasons,
    staleTime: 60 * 60 * 1000, // 1 hour
  });
};

/**
 * Fetcher for country pax details
 */
export const fetchCountriesPaxDetails = async (
  operationDate: Date,
  ownerId: number,
): Promise<any[]> => {
  const encodedDate = encodeDateForUrl(operationDate);
  const response = await operationAPI.getCountriesPaxDetails(
    encodedDate,
    ownerId,
  );
  return response.data;
};

/**
 * React Query hook for country pax details
 */
export const useCountriesPaxDetails = (
  operationDate: Date | null,
  ownerId: number | null,
) => {
  return useQuery({
    queryKey: ['countriesPax', operationDate, ownerId],
    queryFn: () => fetchCountriesPaxDetails(operationDate!, ownerId!),
    enabled: Boolean(operationDate && ownerId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Fetcher for payment breakup details
 */
export const fetchPaymentBreakDetails = async (
  operationDate: Date,
  ownerId: number,
): Promise<any> => {
  const response = await operationAPI.getPaymentBreakupDetails(
    encodeDateForUrl(operationDate),
    ownerId,
  );
  return response.data;
};

/**
 * React Query hook for payment breakup details
 */
export const usePaymentBreakDetails = (
  operationDate: Date | null,
  ownerId: number | null,
) => {
  return useQuery({
    queryKey: ['paymentBreakup', operationDate, ownerId],
    queryFn: () => fetchPaymentBreakDetails(operationDate!, ownerId!),
    enabled: Boolean(operationDate && ownerId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Fetcher for breakfast details
 */
export const fetchBreakfastDetails = async (
  operationDate: string,
  ownerId: number,
): Promise<any> => {
  const response = await operationAPI.getBreakfastDetails(
    operationDate,
    ownerId,
  );
  return response.data;
};

/**
 * React Query hook for breakfast details
 */
export const useBreakfastDetails = (
  operationDate: string | null,
  ownerId: number | null,
) => {
  return useQuery({
    queryKey: ['breakfastDetails', operationDate, ownerId],
    queryFn: () => fetchBreakfastDetails(operationDate!, ownerId!),
    enabled: Boolean(operationDate && ownerId),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetcher for travel forecast details
 */
export const fetchTravelStatusDetails = async (
  operationDate: Date,
  ownerId: number,
): Promise<any> => {
  const fromDate = encodeDateForUrl(operationDate);
  const toDateObj = new Date(operationDate);
  toDateObj.setDate(toDateObj.getDate() + 7);
  const toDate = encodeDateForUrl(toDateObj);
  const response = await operationAPI.getForcastTravelStatus(
    fromDate,
    toDate,
    ownerId,
  );
  return response.data;
};

/**
 * React Query hook for travel forecast details
 */
export const useTravelStatusDetails = (
  operationDate: Date | null,
  ownerId: number | null,
) => {
  return useQuery({
    queryKey: ['travelStatus', operationDate, ownerId],
    queryFn: () => fetchTravelStatusDetails(operationDate!, ownerId!),
    enabled: Boolean(operationDate && ownerId),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetcher for forecast options
 */
export const fetchForecastOptions = async (): Promise<IForecastEntryOption> => {
  const response = await operationAPI.getForcastOptions();
  return response.data;
};

/**
 * React Query hook for forecast options
 */
export const useForecastOptions = () => {
  return useQuery({
    queryKey: ['forecastOptions'],
    queryFn: fetchForecastOptions,
    staleTime: 60 * 60 * 1000,
  });
};

/**
 * Fetcher for weather details
 */
export const fetchWeatherDetails = async (
  operationDate: Date,
  ownerId: number,
): Promise<any> => {
  const encodedDate = encodeDateForUrl(operationDate);
  const response = await operationAPI.getWeatherDetails(encodedDate, ownerId);
  return response.data;
};

/**
 * React Query hook for weather details
 */
export const useWeatherDetails = (
  operationDate: Date | null,
  ownerId: number | null,
) => {
  return useQuery({
    queryKey: ['weatherDetails', operationDate, ownerId],
    queryFn: () => fetchWeatherDetails(operationDate!, ownerId!),
    enabled: Boolean(operationDate && ownerId),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetcher for owner location list
 */
export const fetchOwnerLocations = async (): Promise<any[]> => {
  const response = await operationAPI.getLocationList();
  return response.data;
};

/**
 * React Query hook for owner location list
 */
export const useOwnerLocations = () => {
  return useQuery({
    queryKey: ['ownerLocations'],
    queryFn: fetchOwnerLocations,
    staleTime: 60 * 60 * 1000,
  });
};

// flightQueries.ts or operationQueries.ts

/**
 * Fetcher for operation details
 */
export const fetchOperationDetails = async (
  ownerId: number,
  encodedDate: string,
): Promise<IOperations> => {
  const response = await operationAPI.getOperationDetails(
    ownerId,
    encodedDate,
    encodedDate,
  );
  return response.data;
};

/**
 * React Query hook for operation details
 */
export const useOperationDetails = (
  ownerId: number | null,
  encodedDate: string | null,
) => {
  return useQuery({
    queryKey: ['operationDetails', ownerId, encodedDate],
    queryFn: () => fetchOperationDetails(ownerId!, encodedDate!),
    enabled: Boolean(ownerId && encodedDate),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetcher for driver allocations details
 */
export const fetchStaffAllocation = async (ownerId: number, date: Date) => {
  const encodedDate = encodeDateForUrl(date);
  const response = await operationAPI.getDriverAllocationList(
    ownerId,
    encodedDate,
  );
  return response.data;
};

/**
 * React Query hook for driver staff allocations details
 */
export const useStaffAllocation = (
  ownerId: number | null,
  selectedDate: Date | null,
) => {
  return useQuery({
    queryKey: ['staffAllocation', ownerId, selectedDate],
    queryFn: () => fetchStaffAllocation(ownerId!, selectedDate!),
    enabled: Boolean(ownerId && selectedDate),
    staleTime: 5 * 60 * 1000, // optional: 5 mins
  });
};

/**
 * Fetcher for crew allocations details
 */

export const fetchCrewAllocation = async (
  ownerId: number,
  selectedDate: Date,
): Promise<any[]> => {
  const encodedDate = encodeDateForUrl(selectedDate);
  const response = await operationAPI.getCrewAllocationList(
    ownerId,
    encodedDate,
  );
  return response?.data;
};

/**
 * React Query hook for driver staff allocations details
 */

export const useCrewAllocation = (
  ownerId: number | null,
  selectedDate: Date | null,
) => {
  return useQuery({
    queryKey: ['crewAllocation', ownerId, selectedDate],
    queryFn: () => fetchCrewAllocation(ownerId!, selectedDate!),
    enabled: Boolean(ownerId && selectedDate),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Fetcher function for pilot allocation data
 */

export const fetchPilotAllocation = async (
  ownerId: number,
  selectedDate: Date,
): Promise<any[]> => {
  const encodedDate = encodeDateForUrl(selectedDate);
  const response = await operationAPI.getPilotAllocationList(
    ownerId,
    encodedDate,
  );
  return response?.data;
};

/**
 * Custom hook using React Query to fetch pilot allocation
 */

export const usePilotAllocation = (
  ownerId: number | null,
  selectedDate: Date | null,
) => {
  return useQuery({
    queryKey: ['pilotAllocation', ownerId, selectedDate],
    queryFn: () => fetchPilotAllocation(ownerId!, selectedDate!),
    enabled: Boolean(ownerId && selectedDate), // only fetch if both values are available
    staleTime: 5 * 60 * 1000, // optional: data stays fresh for 5 mins
  });
};

/**
 * Fetcher function for operationChecklist  data
 */

export const fetchOperationChecklist = async (
  ownerId: number,
  selectedDate: string,
): Promise<any[]> => {
  const response = await operationAPI.getOperationCheckListDetails(
    ownerId,
    selectedDate,
  );
  return response?.data;
};

/**
 * Custom hook using React Query to operation checklist
 */
export const useOperationChecklist = (
  ownerId: number,
  selectedDate: string,
) => {
  return useQuery({
    queryKey: ['operationChecklist', ownerId, selectedDate],
    queryFn: () => fetchOperationChecklist(ownerId!, selectedDate!),
    enabled: Boolean(ownerId && selectedDate),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Fetcher function for calender  data
 */

export const fetchCalendarData = async (
  rateId: number,
  ownerId: number,
): Promise<any[]> => {
  const today = new Date();
  const fromDate = addDays(today, 1).toISOString().slice(0, 10);
  const toDate = addDays(today, 14).toISOString().slice(0, 10); // 1 + 13 days
  const response = await calenderApi.getCalendarDetailsByDate(
    fromDate,
    rateId,
    ownerId,
    toDate,
  );

  return response.data;
};

/**
 * Custom hook using React Query to calender details
 */
export const useCalendarData = (
  rateId: number | null,
  ownerId: number | null
) => {
  return useQuery({
    queryKey: ['calendarData', rateId, ownerId],
    queryFn: async () => {
      if (rateId == null || ownerId == null) throw new Error('Missing params');
      return await fetchCalendarData(rateId, ownerId);
    },
  });
};


/**
 * Fetcher function for fetch booking rates  data
 */

export const fetchBookingRates = async (
  operationDate: Date,
  ownerId: number,
  rateId: number,
): Promise<IBookingAvailability> => {
  const encodedDate = encodeDateForUrl(operationDate);
  const response = await operationAPI.getBookingRate(
    encodedDate,
    ownerId,
    rateId,
  );
  return response?.data;
};

/**
 * Custom hook using React Query to booking rates details
 */
export const useBookingRates = (
  selectedDate: Date | null,
  ownerId: number | null,
  rateId: number | null,
) => {
  return useQuery({
    queryKey: ['bookingRates', selectedDate, ownerId, rateId],
    queryFn: () => fetchBookingRates(selectedDate!, ownerId!, rateId!),
    enabled: Boolean(selectedDate && ownerId && rateId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Fetcher function for fetch availability comments  data
 */

export const fetchAvailabilityComments = async (
  opDate: Date,
  ownerId: number,
  days: number,
): Promise<any> => {
  const encodedDate = encodeDateForUrl(opDate);
  const response = await calenderApi.getAvailabilityComments(
    encodedDate,
    ownerId,
    days,
  );
  return response.data;
};

/**
 * Custom hook using React Query to availability comments
 */
export const useAvailabilityComments = (
  opDate: Date | null,
  ownerId: number | null,
  days: number | null,
) => {
  return useQuery({
    queryKey: ['availabilityComments', opDate, ownerId, days],
    queryFn: () => fetchAvailabilityComments(opDate!, ownerId!, days!),
    enabled: Boolean(opDate && ownerId && days),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const fetchTemperatureList = async (): Promise<ITemperature[]> => {
  const response = await operationAPI.getTempList();
  return response.data; // adjust if your API response shape is different
};

export const useTemperatureList = () => {
  return useQuery({
    queryKey: ['temperatureList'],
    queryFn: fetchTemperatureList,
    staleTime: 5 * 60 * 1000, // cache for 5 minutes
  });
};

export const fetchTodayTemperature = async (
  ownerId: number,
  encodedDate: string,
): Promise<ITodayTemperature | null> => {
  const response = await operationAPI.getTodaysTemp(ownerId, encodedDate);
  return response.data; // Adjust this if your response shape is different
};

export const useTodayTemperature = (
  ownerId: number | null,
  encodedDate: string | null,
) => {
  return useQuery({
    queryKey: ['todayTemperature', ownerId, encodedDate],
    queryFn: () => fetchTodayTemperature(ownerId!, encodedDate!),
    enabled: Boolean(ownerId && encodedDate), // avoid fetching with null values
    staleTime: 5 * 60 * 1000, // optional cache time
  });
};
